import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Zap, Plus, ChevronRight, Clock, MessageCircle, Lock, Send, Inbox, X, Video, Mic, Type, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { usePortal, Portal, PortalInvite, PortalMode, IntroType } from "@/hooks/usePortal";
import { formatDistanceToNow } from "date-fns";
import CreatePortalModal from "@/components/portal/CreatePortalModal";
import PortalDetailScreen from "@/components/portal/PortalDetailScreen";
import IntroCapsuleViewer from "@/components/portal/IntroCapsuleViewer";
import { cn } from "@/lib/utils";

const modeLabels: Record<PortalMode, string> = {
  serious: "Serious Dating",
  casual: "Fun & Casual",
  deep: "Deep Talk",
  anonymous: "Anonymous First",
};

const modeColors: Record<PortalMode, string> = {
  serious: "bg-rose-500/10 text-rose-600",
  casual: "bg-amber-500/10 text-amber-600",
  deep: "bg-violet-500/10 text-violet-600",
  anonymous: "bg-slate-500/10 text-slate-600",
};

const statusColors: Record<string, string> = {
  pending: "bg-amber-500/10 text-amber-600",
  accepted: "bg-green-500/10 text-green-600",
  declined: "bg-red-500/10 text-red-600",
  cancelled: "bg-muted text-muted-foreground",
};

const PortalPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { incomingInvites, outgoingInvites, portals, loading, acceptInvite, declineInvite, cancelInvite } = usePortal();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPortalId, setSelectedPortalId] = useState<string | null>(null);
  const [inviteTab, setInviteTab] = useState<"incoming" | "outgoing">("incoming");
  const [prefilledUserId, setPrefilledUserId] = useState<string | undefined>(undefined);
  const [prefilledUserName, setPrefilledUserName] = useState<string | undefined>(undefined);

  // Handle query params: ?open= to open a portal, ?create= to open create modal with prefilled user
  useEffect(() => {
    const openParam = searchParams.get("open");
    const createParam = searchParams.get("create");
    const nameParam = searchParams.get("name");
    
    if (openParam && !loading) {
      const portal = portals.find(p => p.id === openParam);
      if (portal) {
        setSelectedPortalId(openParam);
        // Clear the param from URL
        searchParams.delete("open");
        setSearchParams(searchParams, { replace: true });
      }
    }
    
    if (createParam) {
      setPrefilledUserId(createParam);
      setPrefilledUserName(nameParam ? decodeURIComponent(nameParam) : undefined);
      setShowCreateModal(true);
      // Clear the params from URL
      searchParams.delete("create");
      searchParams.delete("name");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, portals, loading, setSearchParams]);
  
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setPrefilledUserId(undefined);
    setPrefilledUserName(undefined);
  };

  const handleAcceptInvite = async (inviteId: string) => {
    const portalId = await acceptInvite(inviteId);
    if (portalId) {
      setSelectedPortalId(portalId);
    }
  };

  if (selectedPortalId) {
    return (
      <PortalDetailScreen
        portalId={selectedPortalId}
        onBack={() => setSelectedPortalId(null)}
      />
    );
  }

  const totalInvites = incomingInvites.length + outgoingInvites.length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20 pb-24">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20">
              <Zap className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Portal</h1>
              <p className="text-xs text-muted-foreground">Intentional connections</p>
            </div>
          </div>
          <Badge variant="outline" className="text-xs gap-1.5 px-2.5 py-1">
            <Lock className="w-3 h-3" />
            Private · 1-to-1
          </Badge>
        </div>
      </div>

      <div className="px-4 py-6 space-y-6">
        {/* How Portal Works Strip */}
        <div className="p-4 rounded-2xl bg-gradient-to-r from-primary/5 via-secondary/5 to-primary/5 border border-border/30">
          <p className="text-xs font-medium text-muted-foreground mb-3 text-center">How Portal Works</p>
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 flex flex-col items-center text-center">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-1.5">
                <Send className="w-4 h-4 text-primary" />
              </div>
              <span className="text-xs font-medium">Send request</span>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground/50 flex-shrink-0" />
            <div className="flex-1 flex flex-col items-center text-center">
              <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center mb-1.5">
                <MessageCircle className="w-4 h-4 text-secondary" />
              </div>
              <span className="text-xs font-medium">Exchange capsule</span>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground/50 flex-shrink-0" />
            <div className="flex-1 flex flex-col items-center text-center">
              <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center mb-1.5">
                <Lock className="w-4 h-4 text-green-600" />
              </div>
              <span className="text-xs font-medium">Chat unlocks</span>
            </div>
          </div>
        </div>

        {/* Portal Requests Section with Tabs */}
        <section>
          <div className="mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              Portal Requests
              {totalInvites > 0 && (
                <Badge variant="secondary" className="ml-1">{totalInvites}</Badge>
              )}
            </h2>
            <p className="text-xs text-muted-foreground mt-1">People who want to connect intentionally with you.</p>
          </div>

          <Tabs value={inviteTab} onValueChange={(v) => setInviteTab(v as "incoming" | "outgoing")} className="w-full">
            <TabsList className="w-full grid grid-cols-2 mb-4">
              <TabsTrigger value="incoming" className="flex items-center gap-2">
                <Inbox className="w-4 h-4" />
                Invited ({incomingInvites.length})
              </TabsTrigger>
              <TabsTrigger value="outgoing" className="flex items-center gap-2">
                <Send className="w-4 h-4" />
                Sent ({outgoingInvites.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="incoming">
              {loading ? (
                <div className="space-y-3">
                  {[1, 2].map((i) => (
                    <div key={i} className="h-24 rounded-2xl bg-muted/50 animate-pulse" />
                  ))}
                </div>
              ) : incomingInvites.length === 0 ? (
                <div className="py-8 text-center rounded-2xl bg-muted/30 border border-dashed border-border">
                  <Inbox className="w-8 h-8 mx-auto text-muted-foreground/50 mb-2" />
                  <p className="text-muted-foreground text-sm">No Portal invitations yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {incomingInvites.map((invite) => (
                    <IncomingInviteCard
                      key={invite.id}
                      invite={invite}
                      onAccept={() => handleAcceptInvite(invite.id)}
                      onDecline={() => declineInvite(invite.id)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="outgoing">
              {loading ? (
                <div className="space-y-3">
                  {[1, 2].map((i) => (
                    <div key={i} className="h-24 rounded-2xl bg-muted/50 animate-pulse" />
                  ))}
                </div>
              ) : outgoingInvites.length === 0 ? (
                <div className="py-8 text-center rounded-2xl bg-muted/30 border border-dashed border-border">
                  <Send className="w-8 h-8 mx-auto text-muted-foreground/50 mb-2" />
                  <p className="text-muted-foreground text-sm">No sent invitations.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {outgoingInvites.map((invite) => (
                    <OutgoingInviteCard
                      key={invite.id}
                      invite={invite}
                      onCancel={() => cancelInvite(invite.id)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </section>

        {/* Your Active Portals Section */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Your Private Connection Spaces</h2>
            {portals.length > 0 && (
              <Badge variant="outline">{portals.length}</Badge>
            )}
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 rounded-2xl bg-muted/50 animate-pulse" />
              ))}
            </div>
          ) : portals.length === 0 ? (
            <div className="py-10 text-center rounded-2xl bg-muted/20 border border-dashed border-border">
              <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Zap className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Your private connection spaces</h3>
              <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                Portals are where meaningful conversations begin — without small talk.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {portals.map((portal) => (
                <PortalCard
                  key={portal.id}
                  portal={portal}
                  onClick={() => setSelectedPortalId(portal.id)}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Create Portal CTA */}
      <div className="fixed bottom-24 left-0 right-0 px-4 z-30">
        <div className="bg-background/95 backdrop-blur-sm rounded-3xl p-4 shadow-lg border border-border/30">
          <div className="text-center mb-3">
            <p className="text-sm font-medium text-muted-foreground">No swiping. No small talk.</p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="w-full py-6 text-lg font-semibold rounded-full bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 shadow-xl"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Portal
          </Button>
          <div className="mt-3 text-center space-y-1.5">
            <p className="text-xs text-muted-foreground">
              Tip: Start with someone you already matched with.
            </p>
            <p className="text-xs text-muted-foreground/60 italic">
              Suggested first capsule: "What made you want to open this Portal?"
            </p>
          </div>
        </div>
      </div>

      {/* Create Portal Modal */}
      <CreatePortalModal
        open={showCreateModal}
        onClose={handleCloseCreateModal}
        prefilledUserId={prefilledUserId}
        prefilledUserName={prefilledUserName}
      />
    </div>
  );
};

// Incoming Invite Card Component
const IncomingInviteCard = ({
  invite,
  onAccept,
  onDecline,
}: {
  invite: PortalInvite;
  onAccept: () => void;
  onDecline: () => void;
}) => {
  const [showIntroViewer, setShowIntroViewer] = useState(false);
  const hasIntro = !!invite.intro_type;

  const getIntroIcon = () => {
    switch (invite.intro_type) {
      case "video": return <Video className="w-3.5 h-3.5 text-rose-500" />;
      case "voice": return <Mic className="w-3.5 h-3.5 text-violet-500" />;
      case "text": return <Type className="w-3.5 h-3.5 text-blue-500" />;
      default: return null;
    }
  };

  return (
    <>
      <div className="p-4 rounded-2xl bg-card border border-border/50 shadow-sm">
        <div className="flex items-start gap-3">
          {/* Blurred avatar until accepted */}
          <Avatar className="w-12 h-12 blur-sm">
            <AvatarImage src={invite.from_user?.photo_url || undefined} />
            <AvatarFallback>{invite.from_user?.name?.[0] || "?"}</AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium truncate">
                {invite.from_user?.name || "Someone"}
              </span>
              <Badge className={modeColors[invite.mode]} variant="secondary">
                {modeLabels[invite.mode]}
              </Badge>
            </div>
            
            <p className="text-xs text-muted-foreground mb-2">
              Wants to open a private Portal with you.
            </p>
            
            {/* Intro Available Banner - Enhanced */}
            {hasIntro && (
              <div className="mb-3 space-y-2">
                <button
                  onClick={() => setShowIntroViewer(true)}
                  className="w-full p-3 rounded-xl bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 border-2 border-primary/30 hover:border-primary/50 transition-all shadow-sm hover:shadow-md"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20">
                      {getIntroIcon()}
                    </div>
                    <div className="flex-1 text-left">
                      <p className="text-sm font-semibold text-primary">Intro capsule available</p>
                      <p className="text-xs text-muted-foreground">Listen before deciding</p>
                    </div>
                    <div className="p-2 rounded-full bg-primary/20">
                      <Play className="w-4 h-4 text-primary" />
                    </div>
                  </div>
                </button>
                <p className="text-[10px] text-muted-foreground/70 text-center italic">
                  Preview their intro to understand their intention
                </p>
              </div>
            )}
            
            {invite.message && (
              <div className="p-2 rounded-lg bg-muted/50 mb-2">
                <p className="text-xs text-muted-foreground/70 mb-0.5">They shared why:</p>
                <p className="text-sm text-foreground line-clamp-2">
                  "{invite.message}"
                </p>
              </div>
            )}

            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
              <Clock className="w-3 h-3" />
              {formatDistanceToNow(new Date(invite.created_at), { addSuffix: true })}
            </div>

            <div className="space-y-2">
              <div className="flex gap-2">
                <Button
                  onClick={onAccept}
                  size="sm"
                  className="flex-1 rounded-full bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 shadow-lg"
                >
                  <Zap className="w-4 h-4 mr-1" />
                  Enter Portal Together
                </Button>
                <Button
                  onClick={onDecline}
                  size="sm"
                  variant="ghost"
                  className="rounded-full text-muted-foreground hover:text-foreground"
                >
                  Not now
                </Button>
              </div>
              <p className="text-xs text-muted-foreground/60 text-center italic">
                Your intention is received. Chat unlocks after you both exchange a capsule.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Intro Capsule Viewer Modal */}
      {hasIntro && (
        <IntroCapsuleViewer
          open={showIntroViewer}
          onClose={() => setShowIntroViewer(false)}
          introType={invite.intro_type as IntroType}
          introContentUrl={invite.intro_content_url}
          introText={invite.intro_text}
          senderName={invite.from_user?.name || "Someone"}
        />
      )}
    </>
  );
};

// Outgoing Invite Card Component
const OutgoingInviteCard = ({
  invite,
  onCancel,
}: {
  invite: PortalInvite;
  onCancel: () => void;
}) => {
  const isPending = invite.status === "pending";
  const isDeclined = invite.status === "declined";
  const hasIntro = !!invite.intro_type;
  
  const getStatusBadge = () => {
    if (isPending) return "Pending";
    if (isDeclined) return "Not accepted";
    return invite.status.charAt(0).toUpperCase() + invite.status.slice(1);
  };

  const getIntroIcon = () => {
    switch (invite.intro_type) {
      case "video": return <Video className="w-3 h-3 text-rose-500" />;
      case "voice": return <Mic className="w-3 h-3 text-violet-500" />;
      case "text": return <Type className="w-3 h-3 text-blue-500" />;
      default: return null;
    }
  };
  
  return (
    <div className={cn(
      "p-4 rounded-2xl bg-card border shadow-sm",
      isDeclined ? "border-border/30 opacity-75" : "border-border/50"
    )}>
      <div className="flex items-start gap-3">
        {/* Blurred avatar for recipient */}
        <Avatar className="w-12 h-12 blur-sm">
          <AvatarImage src={invite.to_user?.photo_url || undefined} />
          <AvatarFallback>{invite.to_user?.name?.[0] || "?"}</AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium truncate">
              {invite.to_user?.name || "Someone"}
            </span>
            <Badge className={statusColors[invite.status]} variant="secondary">
              {getStatusBadge()}
            </Badge>
          </div>
          
          <div className="flex items-center gap-2 mb-2">
            <Badge className={modeColors[invite.mode]} variant="secondary">
              {modeLabels[invite.mode]}
            </Badge>
            {hasIntro && (
              <Badge variant="outline" className="text-[10px] gap-1 px-1.5 py-0.5">
                {getIntroIcon()}
                Intro sent
              </Badge>
            )}
          </div>

          {invite.message && (
            <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
              "{invite.message}"
            </p>
          )}

          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="w-3 h-3" />
                {formatDistanceToNow(new Date(invite.created_at), { addSuffix: true })}
              </div>
              {isPending && (
                <p className="text-xs text-muted-foreground/60">
                  Waiting for their response.
                </p>
              )}
              {isDeclined && (
                <p className="text-xs text-muted-foreground/60">
                  It's okay — Portal requests are always optional.
                </p>
              )}
            </div>
            
            {isPending && (
              <Button
                onClick={onCancel}
                size="sm"
                variant="ghost"
                className="text-muted-foreground hover:text-destructive rounded-full h-8 px-3"
              >
                <X className="w-4 h-4 mr-1" />
                Cancel request
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Portal Card Component
const PortalCard = ({
  portal,
  onClick,
}: {
  portal: Portal;
  onClick: () => void;
}) => {
  return (
    <button
      onClick={onClick}
      className="w-full p-4 rounded-2xl bg-card border border-border/50 shadow-sm hover:shadow-md hover:border-primary/30 transition-all text-left"
    >
      <div className="flex items-center gap-3">
        <div className="relative">
          <Avatar className="w-12 h-12">
            <AvatarImage src={portal.other_user?.photo_url || undefined} />
            <AvatarFallback>{portal.other_user?.name?.[0] || "?"}</AvatarFallback>
          </Avatar>
          {!portal.chat_unlocked && (
            <div className="absolute -bottom-1 -right-1 p-1 rounded-full bg-amber-500/20">
              <Lock className="w-3 h-3 text-amber-600" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-medium truncate">
              {portal.other_user?.name || "Unknown"}
            </span>
            <Badge className={modeColors[portal.mode]} variant="secondary">
              {modeLabels[portal.mode]}
            </Badge>
          </div>
          
          <div className="flex items-center gap-2 text-xs">
            {portal.chat_unlocked ? (
              <span className="flex items-center gap-1 text-green-600">
                <MessageCircle className="w-3 h-3" />
                Chat unlocked
              </span>
            ) : (
              <span className="flex items-center gap-1 text-amber-600">
                <Lock className="w-3 h-3" />
                Warming up
              </span>
            )}
          </div>
        </div>

        <ChevronRight className="w-5 h-5 text-muted-foreground" />
      </div>
    </button>
  );
};

export default PortalPage;
